import React from 'react';
import { type LaunchDarklyFlags } from '@/common/launchDarkly';
import { useIsMediumScreenMediaQuery } from '../../../lib/isSmallScreen';
import { useShallowSelector, useDispatch } from '../../../lib/reduxHooks';
import validateBlocksAndSendEnvelope from '../../../lib/validateBlocksAndSendEnvelope';
import * as actions from '../../../store/senderBuild/actions';
import { EditEmailModal } from '../EditEmailModal';

export function useEditEmailDialog(args: { flags: LaunchDarklyFlags }) {
  const { flags } = args;
  const { blocks, envelopeId, emailConfig, groups, zoom, selectedBlocks } = useShallowSelector((state) => ({
    blocks: state.senderBuild.blocks,
    envelopeId: state.senderBuild.id,
    emailConfig: state.senderBuild.emailConfig,
    groups: state.senderBuild.groups,
    zoom: state.senderBuild.zoom,
    selectedBlocks: state.senderBuild.selectedBlocks,
  }));

  const dispatch = useDispatch();
  const isMdScreenOrSmaller = useIsMediumScreenMediaQuery();
  const [showModal, setShowModal] = React.useState(false);

  function toggleModal() {
    const emailSubject = emailConfig.subject?.trim() || '';
    const emailBody = emailConfig.body?.trim() || '';

    if (flags?.['hide-build-ui-elements']) {
      const updatedBlocksAndGroups = validateBlocksAndSendEnvelope(blocks, groups, true, zoom);
      if (updatedBlocksAndGroups) {
        dispatch(
          actions.validateBlocksBeforeSending(
            updatedBlocksAndGroups.updatedBlocks,
            updatedBlocksAndGroups.updatedGroups,
            { subject: emailSubject || '', body: emailBody },
            true
          )
        );
      } else {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        dispatch(actions.updateEmailConfig(envelopeId, { subject: emailSubject, body: emailBody }, true));
      }
    } else {
      setShowModal(true);
      if (selectedBlocks.blockIndices.length > 0) {
        dispatch(actions.selectBlocks(selectedBlocks.documentId!, selectedBlocks.pageIndex!, []));
      }
    }
  }

  function renderDialog() {
    if (!showModal) return null;
    return (
      <EditEmailModal
        fullScreen={isMdScreenOrSmaller}
        onClose={() => setShowModal(false)}
        subject={flags?.['show-smart-email'] ? 'Save time with smart emails' : 'Ready to send?'}
        action={isMdScreenOrSmaller ? 'Send' : 'Send for Signatures'}
        send
        envelopeId={envelopeId}
      />
    );
  }

  return {
    renderDialog,
    toggleModal,
  };
}
